import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { SplashAgent } from './SplashAgent';
import { SPLASH_ENABLED } from '../lib/features';

/**
 * Route guard — redirects to /login?next=<path> when the user isn't
 * authenticated. While the auth status is still 'unknown' (boot,
 * waiting on /auth/me), shows a centered spinner so we don't flash a
 * login form to a returning user.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const loc = useLocation();

  if (status === 'unknown') {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (status === 'guest') {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Splash mounts on every authed page. The component itself decides
  // whether to render a FAB or the docked panel based on its own
  // local state — it's safe to include unconditionally inside the
  // gated subtree.
  //
  // ...as long as the deployment actually HAS an assistant. Prod has no
  // ai_chat provider configured, so the FAB was an always-visible button
  // that could only ever return an error (2026-08-08). SPLASH_ENABLED is
  // a build-time flag the prod launcher sets to 0; see lib/features.ts for
  // why it is build-time and what replaces it.
  return (
    <>
      {children}
      {SPLASH_ENABLED && <SplashAgent />}
    </>
  );
}
